import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import {
  CurrentItemIcon,
  EquipmentName,
  EquipmentRowStack,
  NavigationContainer,
  NavigatorTooltip,
  POIName,
  PoiRowStack,
} from '../styles';
import { abyssColors } from '@/theme/colors';
import { usePointOfInterestDetails } from '../../hooks';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { generatePoiDisplayName } from '../../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';

import {
  PointOfInterestsFragment,
  useDeleteAssemblyFromReportMutation,
  useDeletePoiFromReportMutation,
  useUpdateReportOrderMutation,
} from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { DraggableList } from '@/components/shared/DraggableList';
import { ReportOrder } from './types';

const scrollToElement = (id: string) => {
  const element = document.querySelector(`#${CSS.escape(id)}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

type ReportNavigatorProps = {
  setOrderUpdateLoader: (loader: boolean) => void;
  orderOfReport: ReportOrder;
  setOrderOfReport: React.Dispatch<
    React.SetStateAction<{
      assemblyOrder: string[];
      poiOrder: {
        [key: string]: string[];
      };
    }>
  >;
};

type EquipmentItem = {
  id: string;
  name: string;
  [key: string]: unknown;
};

export const ReportNavigator = ({
  setOrderUpdateLoader,
  orderOfReport,
  setOrderOfReport,
}: ReportNavigatorProps) => {
  const setSelectedReport = useSetAtom(state.selectedReport);
  const { assemblies, groupPoiByEquipmentWithNumbers: equipmentAndPOIs } =
    usePointOfInterestDetails();
  const router = useRouter();
  const reportId = router?.query?.reportid as string | undefined;
  const [equipmentOrder, setEquipmentOrder] =
    useState<{ id: string; tagName: string }[]>(assemblies);
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined);
  const [deleteEquipmentId, setDeleteEquipmentId] = useState<string | null>(null);
  const [deletePoiId, setDeletePoiId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [navigatorLoading, setNavigatorLoading] = useState(false);
  const [updatePoiOrder] = useUpdateReportOrderMutation();
  const [deletePoiFromReport] = useDeletePoiFromReportMutation();
  const [deleteAssemblyFromReport] = useDeleteAssemblyFromReportMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  useEffect(() => {
    if (assemblies) {
      setEquipmentOrder(assemblies);
    }
  }, [assemblies]);

  useEffect(() => {
    if (Object.keys(equipmentAndPOIs).length > 0 && !selectedItem) {
      setSelectedItem(Object.keys(equipmentAndPOIs)[0]);
    }
  }, [equipmentAndPOIs, selectedItem]);

  const navbarHeight = useNavbarHeight();

  const assemblyIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    Object.values(equipmentAndPOIs).forEach((pois) => {
      if (pois.length > 0) {
        map[pois[0].assembly.id] = pois[0].assembly.tagName;
      }
    });
    return map;
  }, [equipmentAndPOIs]);

  useEffect(() => {
    if (orderOfReport?.assemblyOrder) {
      setEquipmentOrder((prev) => {
        const assemblyValue = assemblies.find(
          (assembly) => assembly.id === orderOfReport?.assemblyOrder[0]
        );
        return [...prev, { id: assemblyValue?.id ?? '', tagName: assemblyValue?.tagName ?? '' }];
      });
    }
  }, [orderOfReport?.assemblyOrder, assemblies]);

  const handleClick = (item: string) => {
    setSelectedItem(item);
    scrollToElement(item);
  };

  const handleRemoveEquipment = useCallback(() => {
    if (!deleteEquipmentId || !reportId) return;
    setDeleteLoading(true);
    setNavigatorLoading(true);
    deleteAssemblyFromReport({
      variables: {
        input: {
          reportId,
          assemblyId: deleteEquipmentId,
        },
      },
      refetchQueries: ['ReportDetails'],
      awaitRefetchQueries: true,
      onCompleted: () => {
        setDeleteLoading(false);
        setNavigatorLoading(false);
        setDeleteEquipmentId(null);
        showSnackBar('Component and associated POIs removed successfully', 'success');
      },
      onError: (error) => {
        console.error('Failed to remove component from report', error);
        setDeleteLoading(false);
        setNavigatorLoading(false);
        setDeleteEquipmentId(null);
        showSnackBar('Failed to remove component from report', 'error');
      },
    });
  }, [deleteEquipmentId, reportId, deleteAssemblyFromReport, showSnackBar]);

  const handleRemovePoi = useCallback(() => {
    if (!deletePoiId || !reportId) return;
    setDeleteLoading(true);
    setNavigatorLoading(true);
    deletePoiFromReport({
      variables: {
        input: {
          reportId,
          pointOfInterestId: deletePoiId,
        },
      },
      refetchQueries: ['ReportDetails'],
      awaitRefetchQueries: true,
      onCompleted: () => {
        setDeleteLoading(false);
        setNavigatorLoading(false);
        setDeletePoiId(null);
        showSnackBar('POI removed from report successfully', 'success');
      },
      onError: (error) => {
        console.error('Failed to remove POI from report', error);
        setDeleteLoading(false);
        setNavigatorLoading(false);
        setDeletePoiId(null);
        showSnackBar('Failed to remove POI from report', 'error');
      },
    });
  }, [deletePoiId, reportId, deletePoiFromReport, showSnackBar]);

  const getOrderedPOIs = (assemblyId: string): PointOfInterestsFragment[] => {
    const poiIds = orderOfReport?.poiOrder?.[assemblyId] || [];
    const assemblyName = assemblyIdToNameMap[assemblyId];

    let pois: PointOfInterestsFragment[] = [];

    if (assemblyName && equipmentAndPOIs[assemblyName]) {
      pois = equipmentAndPOIs[assemblyName] || [];
    } else {
      Object.values(equipmentAndPOIs).forEach((equipmentPois) => {
        const matchingPois = equipmentPois.filter(
          (poi) => poi.assembly && poi.assembly.id === assemblyId
        );
        if (matchingPois.length > 0) {
          pois = matchingPois;
        }
      });
    }

    if (poiIds.length > 0) {
      const poiMap = new Map(pois.map((poi) => [poi.id, poi]));

      const result = poiIds
        .map((id) => {
          const found = poiMap.get(id);
          return found;
        })
        .filter(Boolean) as PointOfInterestsFragment[];

      return result;
    } else {
      return pois as PointOfInterestsFragment[];
    }
  };

  const equipmentItems: EquipmentItem[] = useMemo(() => {
    if (orderOfReport?.assemblyOrder.length > 0) {
      return orderOfReport.assemblyOrder.map((k: string) => ({
        id: k,
        name: assemblyIdToNameMap[k],
      }));
    }
    return equipmentOrder.map(({ id, tagName }) => ({ id, name: tagName }));
  }, [equipmentOrder, orderOfReport?.assemblyOrder, assemblyIdToNameMap]);

  const buildOrderPayload = (poiOrder: { [key: string]: string[] }): Record<string, string[]> =>
    Object.fromEntries(Object.entries(poiOrder).map(([equip, poiIds]) => [equip, poiIds]));

  const submitReportOrder = useCallback(
    (poiOrder: { [key: string]: string[] }, assemblyOrder: string[]) => {
      if (!reportId) return;
      setOrderUpdateLoader(true);
      const poiOrderObject = buildOrderPayload(poiOrder);
      try {
        updatePoiOrder({
          variables: {
            input: {
              reportId,
              reportOrder: {
                assemblyOrder: assemblyOrder,
                poiOrder: poiOrderObject,
              },
            },
          },
        })
          .then((response) => {
            setSelectedReport((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                reportOrder: {
                  assemblyOrder: response.data?.updateReport?.reportOrder?.assemblyOrder,
                  poiOrder: response.data?.updateReport?.reportOrder?.poiOrder,
                },
              };
            });
          })
          .finally(() => {
            setOrderUpdateLoader(false);
          });
      } catch (e) {
        console.error('Failed to save POI order', e);
      }
    },
    [reportId, updatePoiOrder, setOrderUpdateLoader, setSelectedReport]
  );

  const handleEquipmentOrderChange = useCallback(
    (newOrder: EquipmentItem[]) => {
      const equipmentOrder = newOrder.map((e) => e.id);

      const updatedOrder = {
        ...orderOfReport,
        assemblyOrder: equipmentOrder,
      };
      setEquipmentOrder(
        newOrder.map(({ id, tagName }) => ({ id, tagName: String(tagName || '') }))
      );
      setOrderOfReport(updatedOrder);

      submitReportOrder(orderOfReport?.poiOrder || {}, equipmentOrder);
    },
    [orderOfReport, setEquipmentOrder, setOrderOfReport, submitReportOrder]
  );
  const handlePoiOrderChange = useCallback(
    (assemblyId: string) => (newOrder: PointOfInterestsFragment[]) => {
      if (!assemblyId) return;

      const updated = {
        ...orderOfReport,
        poiOrder: {
          ...orderOfReport?.poiOrder,
          [assemblyId]: newOrder.map((poi) => poi.id),
        },
      };
      setOrderOfReport(updated);
      submitReportOrder(updated.poiOrder, updated.assemblyOrder);
    },
    [orderOfReport, setOrderOfReport, submitReportOrder]
  );

  return (
    <NavigationContainer navbarHeight={navbarHeight} data-testId="report-navigation" sx={{ position: 'relative' }}>
      {navigatorLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
      <Typography
        color={abyssColors.primary[700]}
        fontWeight={700}
        m={3}
        data-testid="report-navigator-title"
      >
        Contents
      </Typography>
      <Stack gap={3} marginBottom={6}>
        <DraggableList
          items={equipmentItems}
          onChange={handleEquipmentOrderChange}
          renderItem={(equip) => {
            const key = equip.id;
            const orderedPOIs = getOrderedPOIs(key);
            return (
              <DraggableList.Item id={key}>
                <Stack key={key} data-testid="report-navigator-equipment">
                  <EquipmentRowStack direction="row" alignItems="center" marginBottom="12px">
                    <CurrentItemIcon selected={selectedItem === key} />
                    <NavigatorTooltip title={equip?.name || ''} placement="top">
                      <EquipmentName
                        selected={selectedItem === key}
                        onClick={() => {
                          handleClick(key);
                        }}
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        {equip?.name || ''}
                      </EquipmentName>
                    </NavigatorTooltip>
                    <IconButton
                      className="delete-btn"
                      size="small"
                      onClick={() => setDeleteEquipmentId(key)}
                      sx={{ padding: '2px' }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: '1.6rem' }} />
                    </IconButton>
                    <DraggableList.DragHandle className="drag-handle" />
                  </EquipmentRowStack>
                  <DraggableList
                    items={orderedPOIs.length > 0 ? orderedPOIs : equipmentAndPOIs[key] || []}
                    onChange={handlePoiOrderChange(key)}
                    renderItem={(point) => (
                      <DraggableList.Item id={point.id}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          marginBottom="4px"
                          data-testid="report-navigator-poi"
                        >
                          <CurrentItemIcon selected={selectedItem === point.id} />
                          <PoiRowStack direction="row" alignItems="center">
                            <NavigatorTooltip
                              title={generatePoiDisplayName({
                                name: point.name,
                                poiType: point.type,
                                poiId: point.poiId,
                              })}
                              placement="top"
                            >
                              <POIName
                                selected={selectedItem === point.id}
                                onClick={() => {
                                  handleClick(point.id);
                                }}
                              >
                                {generatePoiDisplayName({
                                  name: point.name,
                                  poiType: point.type,
                                  poiId: point.poiId,
                                })}
                              </POIName>
                            </NavigatorTooltip>
                            <IconButton
                              className="delete-btn"
                              size="small"
                              onClick={() => setDeletePoiId(point.id)}
                              sx={{ padding: '2px', flexShrink: 0 }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: '1.6rem' }} />
                            </IconButton>
                            <DraggableList.DragHandle className="drag-handle" />
                          </PoiRowStack>
                        </Stack>
                      </DraggableList.Item>
                    )}
                  />
                </Stack>
              </DraggableList.Item>
            );
          }}
        />
      </Stack>

      <Dialog open={deleteEquipmentId !== null} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
          Delete Components and POIs?
        </DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the component and all associated POIs from the report.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 2 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...textButtonStyle }}
            onClick={() => setDeleteEquipmentId(null)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{ ...containedButtonStyle }}
            onClick={handleRemoveEquipment}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deletePoiId !== null} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
          Remove POI from report?
        </DialogTitle>
        <DialogContent>
          <Typography>This action will remove the selected POI from the report.</Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 2 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{ ...textButtonStyle }}
            onClick={() => setDeletePoiId(null)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{ ...containedButtonStyle }}
            onClick={handleRemovePoi}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </NavigationContainer>
  );
};
